/**
 * Copyright (C) Schweizerische Bundesbahnen SBB, 2017.
 *
 * ESTA WebJS: App Module
 *
 * @author u218609 (Kevin Kreuzer)
 * @version: 2.0.0
 * @since 10.05.2017, 2017.
 */
import {CommonModule} from '@angular/common';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {ErrorHandler, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {DateAdapter} from '@sbb-esta/lyne-elements/core/datetime.js';
import {RxStomp} from '@stomp/rx-stomp';
import {OAuthModule, OAuthStorage} from 'angular-oauth2-oidc';
import {ApiModule, Configuration} from 'cargo-hub-ui-customer-api';
import {InternalApiModule, InternalConfiguration} from 'cargo-hub-ui-internal-api';
import {environment} from '../environments/environment';
import {AppComponent} from './app.component';
import {routing} from './app.routes';
import {AuthModule} from './auth/auth.module';
import {SupportUserPartnerIdInterceptor} from './auth/interceptors/support-user-partner-id.interceptor';
import {DynamicInterceptorProxyInterceptor} from './interceptors/dynamic-interceptor-proxy.interceptor';
import {TraceIdInterceptor} from './interceptors/trace-id.interceptor';
import {VersionCheckInterceptor} from './interceptors/version-check.interceptor';
import {QuickActionService} from './quick-actions/quick-action.service';
import {RootStoreModule} from './root-store/root-store.module';
import {OnetrustService} from './services/onetrust/onetrust.service';
import {MonitoringHelper} from './util/monitoring/monitoring-helper';
import {ViewModule} from './view/view.module';
import {CdDateAdapter} from '~/app/services/adapter/CdDateAdapter';

class CdErrorHandler implements ErrorHandler {
  handleError(error) {
    MonitoringHelper.noticeError(error);
  }
}

export function apiConfigFactory() {
  return new Configuration({basePath: environment.endpoints.backendBaseUrl});
}

export function internalApiConfigFactory() {
  return new InternalConfiguration({basePath: environment.endpoints.backendInternalBaseUrl});
}

@NgModule({
  declarations: [],
  imports: [
    AppComponent,
    AuthModule,
    BrowserAnimationsModule,
    BrowserModule,
    CommonModule,
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: [environment.endpoints.backendBaseUrl, environment.endpoints.backendInternalBaseUrl],
        sendAccessToken: true
      }
    }),
    ApiModule.forRoot(apiConfigFactory),
    InternalApiModule.forRoot(internalApiConfigFactory),
    RootStoreModule,
    routing,
    ViewModule,
    ...environment.optionalModules
  ],
  providers: [
    {provide: OAuthStorage, useValue: localStorage},
    // it calls the SecureLinkInterceptor, so ensure it is loaded after OAuthModule, otherwise OAuthModule could reverse changes to the request made by SecureLinkInterceptor
    DynamicInterceptorProxyInterceptor,
    {provide: HTTP_INTERCEPTORS, useExisting: DynamicInterceptorProxyInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: SupportUserPartnerIdInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: TraceIdInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: VersionCheckInterceptor, multi: true},
    {provide: RxStomp, useFactory: () => new RxStomp()},
    {provide: ErrorHandler, useClass: CdErrorHandler},
    {provide: DateAdapter, useClass: CdDateAdapter},
    {provide: Window, useValue: window},
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {
  // noinspection JSUnusedLocalSymbols
  constructor(quickActionService: QuickActionService, onetrustService: OnetrustService) {}
}
